import { useEffect, useState } from "react";
import { useCurrentUser } from "../../../../../providers/CurrentUserProvider";
import { useHistory } from "react-router-dom";
import { getMemberships, createMemberships } from "../../../../../../actions/athlete";
import { RegistrationWidget } from "../../../../../layout/registration/RegistrationWidget";
import { useDispatch } from "react-redux";
import { Container } from "@mui/material";
import { useCreateCheckoutSession } from "../../../../../../hooks/useCreateCheckoutSession";
import { useLocalizationContext } from "../../../../../providers/LocalizationProvider";
import { SHORT_DATE } from "../../../../../../utils/dates";
import dayjs from "dayjs";
import duration from "dayjs/plugin/duration";

dayjs.extend(duration);

export const customDivisionNameAdapter = (series, division) => series.options?.divisions?.find(d => d.id === division.id)?.name || division.name;

const useRegister = (shortName, seriesId) => {
    const dispatch = useDispatch();
    const history = useHistory();
    return values =>
        dispatch(createMemberships(seriesId, values))
            .then(() => history.push(`/${shortName}/series/${seriesId}/sign-on/success`, { registrationSuccessful: true }));
};

export const SeriesRegistrationWrapper = ({ organisation, series }) => {
    const seriesId = series.id;
    const { currentUser } = useCurrentUser();
    const [athletes, setAthletes] = useState();
    const loadRegistrations = () => getMemberships(seriesId).then(response => setAthletes(response.data.athletes));
    useEffect(() => {
        if (currentUser) loadRegistrations();
    }, [currentUser]);

    const register = useRegister(organisation.shortName, seriesId);
    const createCheckoutSession = useCreateCheckoutSession(`payments/Season/${seriesId}/create_checkout_session`);
    const { translate } = useLocalizationContext();

    return (
        <Container>
            <RegistrationWidget
                athletes={athletes?.map(athlete => {
                    const rollingMembership = series.options.rollingMembership;
                    const displayRenewal = !!rollingMembership;
                    const expiryDate = athlete.membership && dayjs(athlete.membership.expiry_date).format(SHORT_DATE);
                    const [subtext, registeredMessage] = athlete.membership ? (displayRenewal ?
                        athlete.membership?.expired ?
                            [
                                translate("membership_is_expired", { expiryDate }),
                                { text: "membership_expired_at", props: { expiryDate } }
                            ] :
                            [
                                translate("membership_expires", { expiryDate }),
                                { text: "membership_will_expire_at", props: { expiryDate, length: rollingMembership.length, unit: rollingMembership.unit } }
                            ]
                        :
                        [
                            translate("Already a member"),
                            { text: "already_a_member", props: { contactEmail: organisation.contactEmail } }
                        ]
                    ) : [];
                    return {
                        id: athlete.id,
                        name: athlete.name,
                        image: athlete.image,
                        dob: athlete.dob,
                        properties: athlete.properties,
                        registration_properties: displayRenewal ? undefined : athlete.registration_properties,
                        registrations_attributes: displayRenewal ? [] : (athlete.membership?.divisions?.map(division => ({
                            id: athlete.membership.id.toString(),
                            option_id: division.id.toString(),
                            name: division.name
                        })) || []),
                        subtext,
                        registeredMessage
                    };
                })}
                options={series.membershipDivisions.map(division => ({
                    optionId: division.id,
                    name: customDivisionNameAdapter(series, division)
                }))}
                registrationOptions={series.registrationOptions}
                captureFields={series.captureFields}
                paymentOptions={series.options}
                register={register}
                createCheckoutSession={createCheckoutSession}
                isSeriesRegistration
            />
        </Container>
    );
};
